/**
 * Bouwstenen voor Markdown-exports (dagboek, momenten): YAML-frontmatter, veilige tekst,
 * bestandsnamen en linkdoelen. Puur, zonder Angular. De exports zijn bedoeld voor
 * notitie-apps (Obsidian e.d.) én om als platte tekst leesbaar te blijven.
 */

export type MdFrontmatterValue = string | number | boolean | readonly (string | number)[] | null | undefined;

/**
 * YAML-frontmatter tussen `---`-regels. Lege waarden (undefined, null, '' en lege lijsten)
 * vallen weg; strings staan altijd tussen dubbele aanhalingstekens zodat dubbele punten,
 * hekjes en datums nooit anders gelezen worden.
 */
export function mdFrontmatter(fields: Readonly<Record<string, MdFrontmatterValue>>): string {
  const lines = ['---'];
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      lines.push(`${key}: [${value.map(yamlScalar).join(', ')}]`);
      continue;
    }
    lines.push(`${key}: ${yamlScalar(value as string | number | boolean)}`);
  }
  lines.push('---');
  return lines.join('\n');
}

function yamlScalar(value: string | number | boolean): string {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\r?\n/g, '\\n')
    .replace(/\t/g, '\\t');
  return `"${escaped}"`;
}

/**
 * Platte tekst (bv. een notitieveld zonder opmaak) als Markdown: regels die per ongeluk
 * een kop, citaat of HTML zouden worden, krijgen een escape; regeleinden blijven staan
 * als harde breaks. Lijstregels (`- ` / `1. `) blijven lijsten.
 */
export function mdPlainText(text: string | null | undefined): string {
  if (!text) return '';
  const lines = normalizeNewlines(text)
    .split('\n')
    .map(line => line.replace(/</g, '&lt;').replace(/^(\s*)(#|>)/, '$1\\$2'));
  return mdHardBreaks(lines.join('\n')).trim();
}

/**
 * Zet harde regeleinden (twee spaties) achter elke tekstregel die door een volgende
 * tekstregel gevolgd wordt, zodat ook renderers die enkele regeleinden samenvoegen de
 * regels van de notitie behouden. Lege regels en lijstregels blijven ongemoeid.
 */
export function mdHardBreaks(markdown: string): string {
  const lines = normalizeNewlines(markdown).split('\n');
  return lines
    .map((line, index) => {
      const next = lines[index + 1];
      if (next === undefined || line.trim() === '' || next.trim() === '') return line;
      if (isListLine(line) || isListLine(next)) return line;
      return `${line.replace(/\s+$/, '')}  `;
    })
    .join('\n');
}

/** Eén regel voor een kop, tabelcel of metaregel: geen regeleinden, geen pipes. */
export function mdInline(text: string | null | undefined): string {
  if (!text) return '';
  return normalizeNewlines(text)
    .replace(/\n+/g, ' ')
    .replace(/</g, '&lt;')
    .replace(/\|/g, '\\|')
    .trim();
}

/** Tekst als citaatblok (`> `), regel per regel; lege regels blijven deel van het citaat. */
export function mdBlockquote(markdown: string): string {
  return normalizeNewlines(markdown)
    .split('\n')
    .map(line => (line.trim() === '' ? '>' : `> ${line}`))
    .join('\n');
}

/** Markdown-tabel; cellen gaan door {@link mdInline}. */
export function mdTable(header: readonly string[], rows: readonly (readonly string[])[]): string {
  const row = (cells: readonly string[]): string => `| ${cells.map(cell => mdInline(cell) || ' ').join(' | ')} |`;
  return [row(header), `|${header.map(() => ' --- ').join('|')}|`, ...rows.map(row)].join('\n');
}

/** Blokken met één lege regel ertussen; lege blokken vallen weg. */
export function mdJoinBlocks(blocks: readonly (string | null | undefined)[]): string {
  return blocks
    .map(block => block?.trim())
    .filter((block): block is string => !!block)
    .join('\n\n');
}

/** Tekens die op Windows, macOS of in notitie-apps niet in een bestandsnaam mogen of breken. */
const UNSAFE_FILE_CHARS = /[\\/:*?"<>|#%[\]^{}]/g;

/** Namen die Windows voor apparaten reserveert, ook met een extensie erachter. */
const WINDOWS_RESERVED_NAMES = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

/**
 * Vrije tekst (een vraag, een momentnaam) als stuk van een bestandsnaam: verboden tekens en
 * stuurtekens (regeleinden, tabs) worden spaties, witruimte wordt samengevoegd en lange tekst
 * wordt op een woordgrens ingekort tot hoogstens `maxLength` tekens. Leestekens die dan aan het
 * einde blijven hangen (komma, punt, koppelteken, open haakje) vallen weg.
 */
export function fileNamePart(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  const printable = [...text].map(char => (char.charCodeAt(0) < 32 ? ' ' : char)).join('');
  const cleaned = printable.replace(UNSAFE_FILE_CHARS, ' ').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLength) return cleaned;
  const cut = cleaned.slice(0, maxLength);
  // Eindigt er precies op de grens een woord, dan is de knip al goed. Anders op de laatste spatie
  // knippen, tenzij dat meer dan de helft zou wegnemen (één heel lang woord: harde knip).
  const endsOnWord = cleaned[maxLength] === ' ';
  const lastSpace = cut.lastIndexOf(' ');
  const atWord = endsOnWord || lastSpace < maxLength / 2 ? cut : cut.slice(0, lastSpace);
  return atWord.replace(/[\s.,;\-–(]+$/, '');
}

/**
 * Veilige bestandsnaam uit vrije tekst (zie {@link fileNamePart}), hoogstens `maxLength` tekens
 * zonder extensie, niet beginnend of eindigend met een punt en nooit een gereserveerde
 * Windows-naam. Leeg wordt `export`.
 */
export function safeFileName(base: string, extension: string, maxLength = 100): string {
  let name = fileNamePart(base, maxLength).replace(/^[\s.]+/, '').replace(/[\s.]+$/, '');
  if (WINDOWS_RESERVED_NAMES.test(name)) name = `_${name}`;
  return `${name || 'export'}.${extension}`;
}

/**
 * Maakt een bestandsnaam uniek binnen `used` (hoofdletterongevoelig, zoals Windows en macOS)
 * door " (2)", " (3)", ... voor de extensie te zetten, en registreert het resultaat.
 */
export function uniqueFileName(fileName: string, used: Set<string>): string {
  const dot = fileName.lastIndexOf('.');
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const extension = dot > 0 ? fileName.slice(dot) : '';
  let candidate = fileName;
  for (let counter = 2; used.has(candidate.toLowerCase()); counter++) {
    candidate = `${stem} (${counter})${extension}`;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

/**
 * Relatief linkdoel voor een Markdown-link of -afbeelding (spaties e.d. procent-gecodeerd).
 * Ook haakjes: een losse "(" uit een vraag in de bestandsnaam brak anders de link in index.md.
 */
export function mdLinkTarget(path: string): string {
  return path
    .split('/')
    .map(segment => encodeURIComponent(segment).replace(/\(/g, '%28').replace(/\)/g, '%29'))
    .join('/');
}

/** Kleine ASCII-slug (kleine letters, cijfers en koppeltekens) voor bv. bijlagenamen. */
export function mdSlug(text: string, maxLength = 40): string {
  const slug = text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.slice(0, maxLength).replace(/-+$/, '') || 'item';
}

function normalizeNewlines(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

function isListLine(line: string): boolean {
  return /^\s*([-*+]|\d+\.)\s+/.test(line);
}
